import { gql } from "@apollo/client";
import Datalist, { DatalistPropsType } from "../../../common/datalist/Datalist";
import {
  BookingAdditionalChargesDatalistQuery,
  BookingAdditionalChargesDatalistQueryVariables,
  BookingAdditionalChargeFilterInput,
  BookingAdditionalChargeSortInput,
  SortEnumType,
  BookingAdditinalChargesAddFromTemplateMutation,
  BookingAdditinalChargesAddFromTemplateMutationVariables
} from "../../../../types/graphql";
import { graph } from "../../../../config/axios-context";
import useAppContext from "utilities/hooks/useAppContext";
import { appendFilters } from "../../../../utilities/datalist/datalist-filter-utilities";
import { uuid } from "config/app-constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar, faCommentsDollar, faDownload, faHashtag, faNoteSticky, faPlus, faSackDollar, faUmbrellaBeach } from "@fortawesome/free-solid-svg-icons";
import * as lodash from 'lodash';
import { Roles } from "utilities/app-utilities";
import BookingAdditionalChargesForm, { BOOKING_ADDITIONAL_CHARGES_DATALIST_FORM } from "components/entities/accommodation/additional-charges/BookingAdditionalChargesForm";
import { useRef, useState } from "react";
import ModalPortal from "components/modal/ModalPortal";
import BookingAdditionalChargesView from "./BookingAdditionalChargesView";
import { useSearchParams } from "react-router-dom";
import Button from "components/common/button/Button";
import Translate from "components/common/Translate";
import MoneyView from "components/common/views/MoneyView";
import AdditionalChargesTemplateDatalist, { ADDITIONAL_CHARGES_TEMPLATE_DATALIST_DEFAULT_COLUMNS, AdditionalChargesTemplateDatalistPropsType } from "./templates/AdditionalChargesTemplateDatalist";
import BookingIdView from "../booking/views/BookingIdView";
import Input from "components/common/form/inputs/Input";
import { deleteMutation } from "utilities/entities/common/delete-mutation";

const BOOKING_ADDITIONAL_CHARGES_DATALIST_VIEW_MODEL = "booking-additional-charges-datalist-view-model";

const query = gql`
query BookingAdditionalChargesDatalist(
  $where: BookingAdditionalChargeFilterInput
  $order: [BookingAdditionalChargeSortInput!]
  $skip: Int
  $take: Int
) {
  bookingAdditionalCharges {
    count(where: $where)
    items(where: $where, skip: $skip, order: $order, take: $take) {
      amount
      date
      name
      id
      note
      isIncome
      booking {
        id
        inSeasonId
      }
    }
    amountSum(where: $where)
  }
}
`;

const addFromTemplateMutation = gql`
mutation BookingAdditinalChargesAddFromTemplate($bookingId: Int!, $templateId: Int!, $count: Int) {
  bookingAdditionalChargeFromTemplate(bookingId: $bookingId, templateId: $templateId, count: $count) {
    id
  }
}
`;

type DataItem = NonNullable<BookingAdditionalChargesDatalistQuery['bookingAdditionalCharges']>['items'][0];

export type BookingAdditionalChargesDatalistProps = {
  config?: Partial<DatalistPropsType<DataItem, BookingAdditionalChargeFilterInput, BookingAdditionalChargeSortInput>>
  name: string,
  fixedFilters?: BookingAdditionalChargeFilterInput,
  bookingId?: number,
  onNewEvent?: (id?: number | null) => void | Promise<void>
}

const BookingAdditionalChargesDatalist = (props: BookingAdditionalChargesDatalistProps) => {
  const context = useAppContext();
  const [additionalChargesCount, setAdditionalChargesCount] = useState<Record<number, number>>({});
  const datalistRef = useRef<Datalist<DataItem, BookingAdditionalChargeFilterInput, BookingAdditionalChargeSortInput>>(null);
  let viewModelName = props.name + BOOKING_ADDITIONAL_CHARGES_DATALIST_VIEW_MODEL;
  const templateModalName = props.name + "booking-additional-charges-datalist-add-template";
  const [searchParams] = useSearchParams();

  const defaultConfig: DatalistPropsType<DataItem, BookingAdditionalChargeFilterInput, BookingAdditionalChargeSortInput> = {
    dataSource: {
      request: async params => {
        try {
          let variables: BookingAdditionalChargesDatalistQueryVariables = {
            order: params.sort,
            skip: params.skip,
            take: params.take,
            where: params.filters
          };

          if (params.searchText) {
            variables.where = appendFilters(variables.where, {
              name: {
                contains: params.searchText
              }
            })
          }

          if (props.fixedFilters) {
            variables.where = appendFilters(variables.where, props.fixedFilters);
          }

          if (props.bookingId) {
            variables.where = appendFilters(variables.where, {
              bookingId: {
                eq: Number(props.bookingId)
              }
            })
          }

          const response = await graph<BookingAdditionalChargesDatalistQuery, BookingAdditionalChargesDatalistQueryVariables>({
            query,
            variables
          });

          if (response.errors) {
            throw response.errors;
          }

          return response.data!.bookingAdditionalCharges!;
        } catch (e: any) {
          context.notify('error', 'request.load.error', Array.isArray(e) ? e[0].message : e.message);
          throw e;
        }
      },
      sort: [
        {
          id: uuid(),
          direction: SortEnumType.Asc,
          name: 'date'
        }
      ]
    },
    itemId: i => i.id,
    name: props.name + "-booking-additional-charges-datalist",
    table: {
      columns: [
        {
          name: 'id',
          card: {
            icon: faHashtag,
            group: 'id'
          }
        },
        {
          name: 'name',
          sort: true,
          filter: {
            type: 'string'
          }
        },
        {
          name: 'amount',
          type: 'number',
          resolve: i => <MoneyView value={i.amount} />,
          sort: true,
          filter: {
            type: 'number'
          },
          card: {
            icon: faSackDollar,
            group: 'amount',
          }
        },
        {
          name: 'date',
          type: 'date',
          sort: true,
          filter: {
            type: 'date'
          },
          card: {
            icon: faCalendar,
            group: 'id',
          }
        },
        {
          name: 'isIncome',
          type: 'boolean',
          sort: true,
          filter: {
            type: 'boolean'
          }
        },
        {
          name: 'booking',
          type: 'number',
          resolve: i => <BookingIdView id={i.booking!.id} inSeasonId={i.booking!.inSeasonId} />,
          card: {
            icon: faUmbrellaBeach,
            group: 'amount',
          }
        },
        {
          name: 'note',
          type: 'string',
          resolve: i => i.note ? <FontAwesomeIcon icon={faNoteSticky} /> : <></>,
          sort: true,
          filter: {
            type: 'nullString'
          },
          card: {
            hide: true,
          }
        }
      ],
      render: {
        actions: {
          mutationPermissionRole: Roles.ACCOMMODATION_BOOKINGS_WRITE,
          delete: async (item: DataItem) => {
            await deleteMutation('bookingAdditionalChargeDelete', item.id);
            datalistRef.current?.reload();
          },
          viewUrl: {
            url: viewModelName,
            type: 'modal',
            params: i => i
          },
          editUrl: {
            url: `booking-view-page${BOOKING_ADDITIONAL_CHARGES_DATALIST_FORM}`,
            type: 'modal',
            params: i => i
          }
        }
      }
    },
    header: {
      rightButtons: props.bookingId && context.userHasRole(Roles.ACCOMMODATION_BOOKINGS_WRITE) ? [
        {
          title: 'button.add',
          icon: faPlus,
          name: 'add',
          onClick: () => {
            context.modal.open(`booking-view-page${BOOKING_ADDITIONAL_CHARGES_DATALIST_FORM}`)
          }
        },
        {
          title: 'button.add-from-template',
          icon: faCommentsDollar,
          name: 'addFromTemplate',
          onClick: () => {
            context.modal.open(templateModalName);
          }
        }
      ] : []
    },
    onItemClick: {
      onClick(item) {
        context.modal.open(viewModelName, item);
      },
      type: 'doubleClick'
    }
  }

  const templateDatalistConfig: AdditionalChargesTemplateDatalistPropsType = {
    name: props.name + 'booing-datalist-template',
    config: {
      onItemClick: undefined,
      table: {
        columns: [
          ...ADDITIONAL_CHARGES_TEMPLATE_DATALIST_DEFAULT_COLUMNS,
          {
            name: "count",
            title: context.t('common.count'),
            type: 'number',
            resolve: i => <Input
              type="number"
              value={additionalChargesCount[i.id]?.toString()}
              onChange={v => {
                setAdditionalChargesCount({
                  ...additionalChargesCount,
                  [i.id]: Number(v)
                })
              }}
              size={4}
              name="count"
            // className="w-20"
            />,
          }
        ],
        render: {
          actions: () => {
            return {
              mutationPermissionRole: false,
              buttons: [
                {
                  title: 'button.select',
                  name: 'select',
                  icon: faDownload,
                  onClick: (item) => {
                    saveFormTemplate(item.id).then();
                  }
                }
              ]
            }
          }
        }
      }
    }
  }

  const config = lodash.merge(defaultConfig, props.config);

  const saveFormTemplate = async (templateId: number) => {
    if (!templateId || !props.bookingId) return;

    try {
      const response = await graph<BookingAdditinalChargesAddFromTemplateMutation, BookingAdditinalChargesAddFromTemplateMutationVariables>({
        query: addFromTemplateMutation,
        variables: {
          bookingId: Number(props.bookingId!),
          templateId: Number(templateId),
          count: additionalChargesCount[templateId] || 1
        }
      });

      if (response.errors) {
        throw response.errors;
      }

      context.modal.close(templateModalName);
      datalistRef.current?.reload();
      context.notify('success', 'request.save.success');
      props.onNewEvent?.(response.data?.bookingAdditionalChargeFromTemplate?.[0]?.id)?.then();
    } catch (error: any) {
      context.notify('error', 'request.save.error', Array.isArray(error) ? error[0].message : error.message);
      throw error;
    }
  }

  return <>
    <Datalist ref={datalistRef} {...config} />
    <ModalPortal name={`booking-view-page${BOOKING_ADDITIONAL_CHARGES_DATALIST_FORM}`} title="pages.accommodation.additionalCharges.form" >
      {searchParams.has(`booking-view-page${BOOKING_ADDITIONAL_CHARGES_DATALIST_FORM}`) ? (
        <BookingAdditionalChargesForm
          bookingId={props.bookingId || 0}
          name="booking-view-page"
          model={context.modal.getData(`booking-view-page${BOOKING_ADDITIONAL_CHARGES_DATALIST_FORM}`)}
          onSave={id => {
            datalistRef.current?.reload();
            context.modal.close(`booking-view-page${BOOKING_ADDITIONAL_CHARGES_DATALIST_FORM}`);
            props.onNewEvent?.(id)?.then();
          }}
          onReset={() => context.modal.close(`booking-view-page${BOOKING_ADDITIONAL_CHARGES_DATALIST_FORM}`)}
        />
      ) : <></>}
    </ModalPortal>
    <ModalPortal name={viewModelName} title="pages.accommodation.additionalCharges.view"   >
      {searchParams.has(viewModelName) ? <>
        <BookingAdditionalChargesView
          name={viewModelName} model={context.modal.getData(viewModelName)}
        />
        <div className="w-full text-right">
          <Button title="button.close" onClick={() => context.modal.close(viewModelName)}>
            <Translate i18nKey="button.close" />
          </Button>
        </div>
      </> : <></>}
    </ModalPortal>
    <ModalPortal name={templateModalName} title="pages.accommodation.additionalChargesTemplate.list">
      {searchParams.has(templateModalName) ? <AdditionalChargesTemplateDatalist {...templateDatalistConfig} /> : <></>}
    </ModalPortal>
  </>
}

export default BookingAdditionalChargesDatalist;
